import { useUsersOptions, type UseUsersOptionsParams } from '@components/ModelSelect/AsyncUserSelect/useUsersOptions';
import { useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useQueryClient } from '@tanstack/react-query';

import type { Nullable } from 'chaika-frontend-types/frontend-cms';

import { primeUserListByIdsCache } from '@user/queries/userQueries';

import { AnyOptionMasculine, AsyncSelect, OptionType } from '@components/Select';
import { isDefined } from '@utils/isDefined';

interface Props extends Omit<UseUsersOptionsParams, 'selectedUserIds'> {
    dataTest?: string;
    disabled?: boolean;
    /** Controlled value. When set, the parent owns the selection state. */
    value?: Nullable<string>;
    /**
     * Uncontrolled initial value. When set, the component owns the selection
     * state and `onChange` is the only signal the parent receives.
     * Cannot be combined with `value` (controlled mode).
     */
    defaultValue?: Nullable<string>;
    title?: string;
    placeholder?: string;
    onChange(userOption: OptionType<string>): void;
    sortUsersWith?(managers: OptionType[]): OptionType[];
}

export const AsyncUserSelect = observer(
    ({ dataTest, title, placeholder, value, defaultValue, disabled, onChange, sortUsersWith, ...props }: Props) => {
        const controlled = isDefined(value);

        if (controlled && isDefined(defaultValue)) {
            throw new Error(
                `Invariant violation: AsyncUserSelect received both 'value' and 'defaultValue'. Use 'value' for controlled mode (parent owns state) or 'defaultValue' for uncontrolled mode (component owns state), never both.`,
            );
        }

        const [internalValue, setInternalValue] = useState<Nullable<string> | undefined>(defaultValue);
        const currentValue = controlled ? value : internalValue;

        const selectedUserIds = useMemo(
            () => (typeof currentValue === 'string' && currentValue !== '' ? [currentValue] : []),
            [currentValue],
        );
        const { defaultOptions, loadOptions, selectedOptions } = useUsersOptions({ ...props, selectedUserIds });

        const queryClient = useQueryClient();
        const handleChange = useCallback(
            (option: OptionType<string>) => {
                if (!controlled) {
                    setInternalValue(option.value);
                }
                onChange(option);
                if (typeof option.value === 'string' && option.value !== '') {
                    primeUserListByIdsCache(queryClient, [option.value]);
                }
            },
            [controlled, onChange, queryClient],
        );

        const decoratedDefaultOptions = sortUsersWith ? sortUsersWith(defaultOptions) : defaultOptions;
        const decoratedLoadOptions = useCallback(
            async (name: string) => {
                const opts = await loadOptions(name);
                return sortUsersWith ? sortUsersWith(opts) : opts;
            },
            [loadOptions, sortUsersWith],
        );

        // currentValue === null happens when the consumer picked the "Any" option
        // (its value is null); render it explicitly because it's not a real user.
        const selectValue = currentValue === null ? AnyOptionMasculine : selectedOptions[0];

        return (
            <AsyncSelect
                hideSelectedOptions
                dataTest={dataTest}
                disabled={disabled}
                title={title}
                placeholder={placeholder}
                value={selectValue}
                defaultOptions={decoratedDefaultOptions}
                loadOptions={decoratedLoadOptions}
                onChange={handleChange}
            />
        );
    },
);
